import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import useSystemRequirements from '../../hooks/useSystemRequirement.js';

const backdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
};

const modalStyle = {
    background: '#fff',
    borderRadius: '12px',
    padding: '30px',
    width: '90%',
    maxWidth: '600px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '20px'
};

const thTdStyle = {
    padding: '10px',
    borderBottom: '1px solid #ddd',
    textAlign: 'left'
};

const getStatusFromScore = (score) => {
    const normalized = parseInt(score?.toString().split('/')[0], 10);
    switch (normalized) {
        case 3:
            return 'Great';
        case 2:
            return 'OK';
        case 1:
        default:
            return 'Needs Improvement';
    }
};

const getColorFromScore = (score) => {
    const normalized = parseInt(score?.toString().split('/')[0], 10);
    switch (normalized) {
        case 3:
            return '#007bff'; // blue
        case 2:
            return '#28a745'; // green
        case 1:
        default:
            return '#d9534f'; // red
    }
};

const MachineSpecsModal = ({ show, onClose, computerId }) => {
    const [specs, setSpecs] = useState();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);

    const { getSystemScoreByComputerId } = useSystemRequirements();

    useEffect(() => {
        const getData = async () => {
            setLoading(true); // reset loading each time modal opens
            const specsScore = await getSystemScoreByComputerId(computerId);
            setSpecs(specsScore);
            setLoading(false);

            // Extract download/upload/ping from the string
            const internetDetails = specsScore.scores?.internet?.name || '';
            const downloadMatch = internetDetails.match(
                /Download:\s*(\d+(\.\d+)?)\s*Mbps/i
            );
            const uploadMatch = internetDetails.match(
                /Upload:\s*(\d+(\.\d+)?)\s*Mbps/i
            );
            const pingMatch = internetDetails.match(
                /Ping:\s*(\d+(\.\d+)?)\s*ms/i
            );

            const download = downloadMatch ? downloadMatch[1] : '0';
            const upload = uploadMatch ? uploadMatch[1] : '0';
            const ping = pingMatch ? pingMatch[1] : '0';
            setRows([
                {
                    category: 'OS',
                    spec: specsScore.specs.os,
                    score: specsScore.os
                },
                {
                    category: 'CPU',
                    spec: specsScore.specs.cpu,
                    score: specsScore.cpu
                },
                {
                    category: 'RAM',
                    spec: `${(specsScore.specs.ram / 1024).toFixed(1)} GB`,
                    score: specsScore.ram
                },
                {
                    category: 'Storage',
                    spec: `${(specsScore.specs.disk.used / 1024).toFixed(
                        1
                    )} GB Used / ${(specsScore.specs.disk.free / 1024).toFixed(
                        1
                    )} GB Free (${(
                        (specsScore.specs.disk.free /
                            (specsScore.specs.disk.used +
                                specsScore.specs.disk.free)) *
                        100
                    ).toFixed(1)}% Free)`,
                    score: specsScore.disk
                },
                {
                    category: 'Internet Speed',
                    spec: `Download: ${download} Mbps / Upload: ${upload} Mbps / Ping: ${ping} ms`,
                    score: specsScore.internet
                }
            ]);
        };

        if (show) {
            getData();
        }
    }, [show]);

    let overallStatus = '';
    let overallColor = '';
    const statusPercent = specs?.statusPercentage || 0;

    if (statusPercent < 60) {
        overallStatus = 'Requires Immediate Attention';
        overallColor = '#C62300';
    } else if (statusPercent < 85) {
        overallStatus = 'Operational with Limitations';
        overallColor = '#1F7D53';
    } else {
        overallStatus = 'Optimized for Productivity';
        overallColor = '#0118D8';
    }

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    style={backdropStyle}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}>
                    <motion.div
                        style={modalStyle}
                        initial={{ y: '-100vh' }}
                        animate={{ y: 0 }}
                        exit={{ y: '-100vh' }}
                        transition={{ type: 'spring', stiffness: 100 }}>
                        <h3 style={{ marginTop: 0, color: '#181fb4' }}>
                            Machine Specs / Scoring
                        </h3>

                        {loading ? (
                            <p>Loading...</p>
                        ) : (
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={thTdStyle}>Category</th>
                                        <th style={thTdStyle}>
                                            Hardware Specification
                                        </th>
                                        <th style={thTdStyle}>Score</th>
                                        <th style={thTdStyle}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, i) => (
                                        <tr key={i}>
                                            <td style={thTdStyle}>
                                                {row.category}
                                            </td>
                                            <td
                                                style={{
                                                    ...thTdStyle,
                                                    whiteSpace: 'pre-wrap'
                                                }}>
                                                {row.spec}
                                            </td>
                                            <td style={thTdStyle}>
                                                {row.score}/3
                                            </td>
                                            <td
                                                style={{
                                                    ...thTdStyle,
                                                    fontWeight: 'bold',
                                                    color: getColorFromScore(
                                                        row.score
                                                    )
                                                }}>
                                                {getStatusFromScore(row.score)}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr
                                        style={{
                                            backgroundColor: '#e0f1ff',
                                            fontWeight: 'bold',
                                            color: overallColor
                                        }}>
                                        <td style={thTdStyle}>Overall Score</td>
                                        <td style={thTdStyle}></td>
                                        <td style={thTdStyle}>
                                            {specs?.scoreText}
                                        </td>
                                        <td style={thTdStyle}>
                                            {specs?.statusPercentage} –{' '}
                                            {overallStatus}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        )}

                        <div style={{ marginTop: '20px', textAlign: 'right' }}>
                            <button
                                onClick={onClose}
                                style={{
                                    backgroundColor: '#E52020',
                                    color: '#fff',
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}>
                                Close
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
MachineSpecsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    computerId: PropTypes.string
};

export default MachineSpecsModal;
